import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  makeStyles, Button, IconButton, Paper, Table, TableBody, TableCell,
  TableHead, TableRow, TextField, Dialog, DialogActions, DialogContent,
  DialogTitle, Grid, Select, MenuItem, InputLabel, FormControl, Tooltip,
  Typography, Chip, Tabs, Tab, Box,
} from "@material-ui/core";
import { Add, Edit, Delete, Info } from "@material-ui/icons";
import api from "../../services/api";
import { toast } from "react-toastify";
import ConfirmationModal from "../../components/ConfirmationModal";

const useStyles = makeStyles((theme) => ({
  root: { padding: theme.spacing(2) },
  toolbar: { display: "flex", gap: theme.spacing(1), marginBottom: theme.spacing(2), alignItems: "center" },
  paper: { overflowX: "auto" },
  table: { minWidth: 600 },
  varBox: {
    background: theme.palette.grey[100],
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 4,
    padding: theme.spacing(1, 1.5),
    marginBottom: theme.spacing(2),
  },
  chip: { margin: 2, cursor: "pointer", fontFamily: "monospace" },
  tabPanel: { paddingTop: theme.spacing(2) },
}));

const STATUS_LIST = [
  { value: "rascunho", label: "Rascunho" },
  { value: "enviada", label: "Enviada" },
  { value: "em_negociacao", label: "Em Negociação" },
  { value: "aprovada", label: "Aprovada" },
];

const VARIABLES = [
  { key: "{{cliente_nome}}", label: "Nome do cliente" },
  { key: "{{cliente_empresa}}", label: "Empresa" },
  { key: "{{cliente_documento}}", label: "CPF/CNPJ" },
  { key: "{{cliente_email}}", label: "Email" },
  { key: "{{cliente_telefone}}", label: "Telefone" },
  { key: "{{cliente_cidade}}", label: "Cidade/UF" },
  { key: "{{data_hoje}}", label: "Data de hoje" },
  { key: "{{data_validade}}", label: "Data de validade" },
];

const makeEmptyForm = () => ({
  name: "", title: "", validDays: 30, defaultStatus: "rascunho",
  paymentTerms: "", intro: "", notes: "", orientacoes: "", termos: "",
});

function TabPanel({ children, value, index }) {
  return value === index ? <Box>{children}</Box> : null;
}

export default function ProposalTemplates() {
  const classes = useStyles();
  const [templates, setTemplates] = useState([]);
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState(0);
  const [form, setForm] = useState(makeEmptyForm());
  const [editing, setEditing] = useState(null);
  const [deleteModal, setDeleteModal] = useState({ open: false, id: null, name: "" });
  const [activeField, setActiveField] = useState(null);
  const formRef = useRef(form);
  formRef.current = form;
  const fieldRefs = useRef({});

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/proposal-templates");
      setTemplates(Array.isArray(data) ? data : []);
    } catch { toast.error("Erro ao carregar modelos"); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleOpen = async (template = null) => {
    setEditing(template);
    setTab(0);
    if (template) {
      try {
        const { data } = await api.get(`/proposal-templates/${template.id}`);
        setForm({
          name: data.name || "",
          title: data.title || "",
          validDays: data.validDays || 30,
          defaultStatus: data.defaultStatus || "rascunho",
          paymentTerms: data.paymentTerms || "",
          intro: data.intro || "",
          notes: data.notes || "",
          orientacoes: data.orientacoes || "",
          termos: data.termos || "",
        });
      } catch { toast.error("Erro ao carregar modelo"); return; }
    } else {
      setForm(makeEmptyForm());
    }
    setOpen(true);
  };

  const handleSave = async () => {
    const current = formRef.current;
    if (!current.name) { toast.warning("Informe o nome do modelo"); return; }
    try {
      if (editing) {
        await api.put(`/proposal-templates/${editing.id}`, current);
        toast.success("Modelo atualizado");
      } else {
        await api.post("/proposal-templates", current);
        toast.success("Modelo criado");
      }
      setOpen(false);
      load();
    } catch { toast.error("Erro ao salvar modelo"); }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/proposal-templates/${deleteModal.id}`);
      toast.success("Modelo deletado");
      setDeleteModal({ open: false, id: null, name: "" });
      load();
    } catch { toast.error("Erro ao deletar modelo"); }
  };

  const setField = (key) => (e) => {
    const value = e.target.value;
    setForm({ ...formRef.current, [key]: value });
  };

  const insertVariable = (variable) => {
    const key = activeField;
    if (!key) { toast.info("Clique em um campo de texto e depois na variável"); return; }
    const current = formRef.current;
    const currentValue = current[key] || "";
    const el = fieldRefs.current[key];
    if (el) {
      const start = el.selectionStart != null ? el.selectionStart : currentValue.length;
      const end = el.selectionEnd != null ? el.selectionEnd : currentValue.length;
      const newValue = currentValue.slice(0, start) + variable + currentValue.slice(end);
      setForm({ ...current, [key]: newValue });
      setTimeout(() => {
        el.focus();
        el.setSelectionRange(start + variable.length, start + variable.length);
      }, 10);
    } else {
      setForm({ ...current, [key]: currentValue + variable });
    }
  };

  const textField = (key, label, rows = 8, placeholder = "") => (
    <TextField
      fullWidth label={label} value={form[key]}
      onChange={setField(key)} variant="outlined" size="small"
      multiline rows={rows} placeholder={placeholder}
      onFocus={() => setActiveField(key)}
      inputRef={(el) => { fieldRefs.current[key] = el; }}
    />
  );

  return (
    <div className={classes.root}>
      <Typography variant="h5" gutterBottom>Modelos de Proposta</Typography>

      <div className={classes.toolbar}>
        <Button variant="contained" color="primary" startIcon={<Add />} onClick={() => handleOpen()}>
          Novo Modelo
        </Button>
      </div>

      <Paper className={classes.paper}>
        <Table className={classes.table} size="small">
          <TableHead>
            <TableRow>
              <TableCell>Nome do Modelo</TableCell>
              <TableCell>Título da Proposta</TableCell>
              <TableCell>Validade (dias)</TableCell>
              <TableCell>Status padrão</TableCell>
              <TableCell align="center">Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {templates.map((t) => (
              <TableRow key={t.id} hover>
                <TableCell><strong>{t.name}</strong></TableCell>
                <TableCell>{t.title || "—"}</TableCell>
                <TableCell>{t.validDays || "—"}</TableCell>
                <TableCell>{STATUS_LIST.find((s) => s.value === t.defaultStatus)?.label || t.defaultStatus}</TableCell>
                <TableCell align="center">
                  <Tooltip title="Editar"><IconButton size="small" onClick={() => handleOpen(t)}><Edit fontSize="small" /></IconButton></Tooltip>
                  <Tooltip title="Excluir"><IconButton size="small" onClick={() => setDeleteModal({ open: true, id: t.id, name: t.name })}><Delete fontSize="small" /></IconButton></Tooltip>
                </TableCell>
              </TableRow>
            ))}
            {templates.length === 0 && (
              <TableRow><TableCell colSpan={5} align="center">Nenhum modelo cadastrado</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>

      {open && (
        <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="md">
          <DialogTitle>{editing ? `Editar Modelo: ${editing.name}` : "Novo Modelo de Proposta"}</DialogTitle>
          <DialogContent>

            <div className={classes.varBox}>
              <Typography variant="caption" style={{ display: "flex", alignItems: "center", gap: 4, marginBottom: 6 }}>
                <Info fontSize="inherit" /> Clique em um campo de texto abaixo e depois em uma variável para inseri-la:
              </Typography>
              <div>
                {VARIABLES.map((v) => (
                  <Chip
                    key={v.key} label={v.key} size="small"
                    className={classes.chip} variant="outlined" color="primary"
                    onClick={() => insertVariable(v.key)}
                    title={v.label}
                  />
                ))}
              </div>
            </div>

            <Paper variant="outlined">
              <Tabs
                value={tab} onChange={(_, v) => setTab(v)}
                indicatorColor="primary" textColor="primary"
                variant="scrollable" scrollButtons="auto"
              >
                <Tab label="Dados Básicos" />
                <Tab label="Apresentação" />
                <Tab label="Observações" />
                <Tab label="Orientações" />
                <Tab label="Termos e Condições" />
              </Tabs>
            </Paper>

            <Box className={classes.tabPanel}>
              <TabPanel value={tab} index={0}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={5}>
                    <TextField
                      fullWidth label="Nome do Modelo *" value={form.name}
                      onChange={setField("name")} variant="outlined" size="small" autoFocus
                    />
                  </Grid>
                  <Grid item xs={12} sm={5}>
                    <TextField
                      fullWidth label="Título da Proposta" value={form.title}
                      onChange={setField("title")} variant="outlined" size="small"
                      placeholder="Ex: Proposta para {{cliente_nome}}"
                      onFocus={() => setActiveField("title")}
                      inputRef={(el) => { fieldRefs.current["title"] = el; }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={2}>
                    <TextField
                      fullWidth label="Validade (dias)" value={form.validDays}
                      onChange={setField("validDays")} variant="outlined" size="small"
                      type="number" inputProps={{ min: 1 }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <FormControl fullWidth variant="outlined" size="small">
                      <InputLabel>Status padrão</InputLabel>
                      <Select label="Status padrão" value={form.defaultStatus} onChange={setField("defaultStatus")}>
                        {STATUS_LIST.map((s) => <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>)}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={8}>
                    <TextField
                      fullWidth label="Condições de Pagamento" value={form.paymentTerms}
                      onChange={setField("paymentTerms")} variant="outlined" size="small"
                      placeholder="Ex: 30/60/90 dias"
                      onFocus={() => setActiveField("paymentTerms")}
                      inputRef={(el) => { fieldRefs.current["paymentTerms"] = el; }}
                    />
                  </Grid>
                </Grid>
              </TabPanel>

              <TabPanel value={tab} index={1}>
                <Typography variant="body2" color="textSecondary" style={{ marginBottom: 8 }}>
                  Texto de apresentação / carta de cobertura que aparecerá no início da proposta.
                </Typography>
                {textField("intro", "Apresentação", 10,
                  "Prezado(a) {{cliente_nome}},\n\nVim por meio desta apresentar nossa proposta comercial...")}
              </TabPanel>

              <TabPanel value={tab} index={2}>
                <Typography variant="body2" color="textSecondary" style={{ marginBottom: 8 }}>
                  Observações gerais sobre os serviços, escopo ou condições especiais.
                </Typography>
                {textField("notes", "Observações", 10,
                  "Descreva os serviços, metodologia e escopo de atendimento...")}
              </TabPanel>

              <TabPanel value={tab} index={3}>
                <Typography variant="body2" color="textSecondary" style={{ marginBottom: 8 }}>
                  Orientações ao cliente: instruções antes, durante e após a execução do serviço.
                </Typography>
                {textField("orientacoes", "Orientações ao Cliente", 12,
                  "ANTES DO SERVIÇO:\n• ...\n\nDURANTE O SERVIÇO:\n• ...\n\nAPÓS O SERVIÇO:\n• ...")}
              </TabPanel>

              <TabPanel value={tab} index={4}>
                <Typography variant="body2" color="textSecondary" style={{ marginBottom: 8 }}>
                  Termos e condições gerais, responsabilidades, garantias e avisos legais.
                </Typography>
                {textField("termos", "Termos e Condições", 12,
                  "ATENÇÃO:\n• Os produtos utilizados são registrados no Ministério da Saúde...\n• ...")}
              </TabPanel>
            </Box>

          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={handleSave} color="primary" variant="contained" disabled={!form.name}>Salvar Modelo</Button>
          </DialogActions>
        </Dialog>
      )}

      <ConfirmationModal
        title="Excluir Modelo"
        open={deleteModal.open}
        onClose={() => setDeleteModal({ open: false, id: null, name: "" })}
        onConfirm={handleDelete}
      >
        Deseja excluir o modelo <strong>{deleteModal.name}</strong>?
      </ConfirmationModal>
    </div>
  );
}
